import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Alert as MuiAlert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import DevicesIcon from "@mui/icons-material/Devices";
import { api } from "../../utils/api";
import { endpoints } from "../../utils/endpoints";
import { describeClient } from "../../utils/common";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return String(value);
  }
};

const ActiveDevices = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const notify = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  const loadSessions = useCallback(() => {
    setLoading(true);
    api.getApiCall(
      endpoints.activeSessions,
      "",
      (response) => {
        const result = response?.data?.result || [];
        setSessions(Array.isArray(result) ? result : []);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        notify(error?.data?.message || "Failed to load active devices", "error");
      }
    );
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const logoutOthers = () => {
    setBusy(true);
    api.postApiCall(
      endpoints.logoutOthers,
      {},
      (response) => {
        setBusy(false);
        const count = response?.data?.result?.revokedSessions ?? 0;
        notify(`Logged out of ${count} other device(s)`, "success");
        loadSessions();
      },
      (error) => {
        setBusy(false);
        notify(error?.data?.message || "Could not log out other devices", "error");
      }
    );
  };

  const logoutDevice = (sessionId) => {
    setBusy(true);
    api.postApiCall(
      endpoints.logoutDevice,
      { sessionId },
      () => {
        setBusy(false);
        notify("Device logged out", "success");
        loadSessions();
      },
      (error) => {
        setBusy(false);
        notify(error?.data?.message || "Could not log out this device", "error");
      }
    );
  };

  const otherCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DevicesIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Active Devices</Typography>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={logoutOthers}
          disabled={busy || otherCount === 0}
        >
          Log out other devices{otherCount ? ` (${otherCount})` : ""}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These are the devices currently signed in to your account. If you don't recognise one, log it out.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Device</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Device ID</TableCell>
              <TableCell>Last Active</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  No active sessions found.
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((s) => (
                <TableRow key={s.sessionId || s.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {describeClient(s.userAgent)}
                      {s.isCurrent && (
                        <Chip label="This device" color="success" size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{s.ipAddress || "-"}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {s.deviceId ? String(s.deviceId).slice(0, 12) + "…" : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(s.lastSeenAt || s.createdDate)}</TableCell>
                  <TableCell align="right">
                    {s.isCurrent ? (
                      <Typography variant="caption" color="text.secondary">Current</Typography>
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        disabled={busy}
                        onClick={() => logoutDevice(s.sessionId)}
                      >
                        Log out
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snack.severity}
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
        >
          {snack.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default ActiveDevices;
